/**
 * @mui/x-chat-headless v9.0.0-alpha.5
 *
 * @license MIT
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
// Local imports for the namespace convenience objects below.
import { ChatLayout, ChatRoot } from "./chat/index.js";
import { ConversationHeader, ConversationHeaderActions, ConversationRoot, ConversationSubtitle, ConversationTitle } from "./conversation/index.js";
import { ComposerAttachButton, ComposerAttachmentList, ComposerHelperText, ComposerRoot, ComposerSendButton, ComposerTextArea, ComposerToolbar } from "./composer/index.js";
import { ConversationListRoot } from "./conversation-list/index.js";
import { ScrollToBottomAffordance, TypingIndicator, UnreadMarker } from "./indicators/index.js";
import { MessageActions, MessageAvatar, MessageContent, MessageMeta, MessageRoot } from "./message/index.js";
import { MessageListDateDivider, MessageListRoot } from "./message-list/index.js";

// Core logic exports
export { ChatProvider } from "./ChatProvider.js";
export { useChat } from "./hooks/useChat.js";
export { useChatComposer } from "./hooks/useChatComposer.js";
export { useChatOnToolCall } from "./hooks/useChatOnToolCall.js";
export { useChatPartRenderer } from "./hooks/useChatPartRenderer.js";
export { useChatStatus } from "./hooks/useChatStatus.js";
export { useChatStore } from "./hooks/useChatStore.js";
export { useConversation, useConversations } from "./hooks/useConversation.js";
export { useMessage, useMessageIds } from "./hooks/useMessage.js";
export { useMessageError } from "./hooks/useMessageError.js";
export { chatSelectors, selectMessageIds, selectMessagesById, selectConversationIds, selectConversationsById, selectActiveConversationId, selectIsStreaming, selectHasMoreHistory, selectError, selectMessages, selectMessage, selectMessageError, selectConversations, selectConversation, selectActiveConversation, selectMessageCount, selectConversationCount, selectComposerValue, selectComposerAttachments, selectTypingUserIds, selectTypingUserIdsForActiveConversation } from "./selectors/chatSelectors.js";
export { createEchoAdapter } from "./adapters/createEchoAdapter.js";
export { createAiSdkAdapter } from "./adapters/createAiSdkAdapter.js";
export { ChatStreamError } from "./stream/ChatStreamError.js";

// Headless component exports (structural primitives without styling)
// Using direct re-exports so the API docs builder can resolve component symbols correctly.
export { ChatLayout, ChatRoot, useChatLocaleText } from "./chat/index.js";
export { ConversationHeader, ConversationHeaderActions, ConversationHeaderInfo, ConversationRoot, ConversationSubtitle, ConversationTitle } from "./conversation/index.js";
export { ComposerAttachButton, ComposerAttachmentList, ComposerHelperText, ComposerLabel, ComposerRoot, ComposerSendButton, ComposerTextArea, ComposerToolbar, useComposerContext } from "./composer/index.js";
export { ConversationListItem, ConversationListItemActions, ConversationListItemAvatar, ConversationListItemContent, ConversationListPreview, ConversationListRoot, ConversationListTimestamp, ConversationListTitle, ConversationListUnreadBadge } from "./conversation-list/index.js";
export { ScrollToBottomAffordance, TypingIndicator, UnreadMarker } from "./indicators/index.js";
export { FilePart, MessageActions, MessageActionsMenu, MessageActionsMenuGroup, MessageActionsMenuGroupLabel, MessageActionsMenuItem, MessageActionsMenuPopup, MessageActionsMenuPositioner, MessageActionsMenuRoot, MessageActionsMenuTrigger, MessageAuthorLabel, MessageAvatar, MessageContent, MessageError, MessageMeta, MessageRoot, ReasoningPart, SourceDocumentPart, SourceUrlPart, ToolPart, createFilePartRenderer, createReasoningPartRenderer, createSourceDocumentPartRenderer, createSourceUrlPartRenderer, createToolPartRenderer } from "./message/index.js";
export { getDefaultMessagePartRenderer } from "./message/defaultMessagePartRenderers.js";
export { MessageGroup, createTimeWindowGroupKey } from "./message-group/index.js";
export { SuggestionItem, SuggestionsRoot } from "./suggestions/index.js";
export { MessageListDateDivider, MessageListRoot, useMessageListContext } from "./message-list/index.js";

// OwnerState types

// Part renderer OwnerState types are exported via the type block above (FilePartOwnerState, etc.)

// Locale types

// Variant types

export { ChatVariantProvider, useChatVariant } from "./chat/index.js";

// Density types

export { ChatDensityProvider, useChatDensity } from "./chat/index.js";

// Namespace exports for dot-notation usage (e.g. <Chat.Root />, <Message.Avatar />)
export const Chat = {
  Root: ChatRoot,
  Layout: ChatLayout
};
export const Conversation = {
  Root: ConversationRoot,
  Header: ConversationHeader,
  HeaderActions: ConversationHeaderActions,
  Title: ConversationTitle,
  Subtitle: ConversationSubtitle
};
export const ConversationList = {
  Root: ConversationListRoot
};
export const Composer = {
  Root: ComposerRoot,
  TextArea: ComposerTextArea,
  SendButton: ComposerSendButton,
  AttachButton: ComposerAttachButton,
  AttachmentList: ComposerAttachmentList,
  Toolbar: ComposerToolbar,
  HelperText: ComposerHelperText
};
export const Message = {
  Root: MessageRoot,
  Avatar: MessageAvatar,
  Content: MessageContent,
  Meta: MessageMeta,
  Actions: MessageActions
};
export const MessageList = {
  Root: MessageListRoot,
  DateDivider: MessageListDateDivider
};
export const Indicators = {
  TypingIndicator,
  UnreadMarker,
  ScrollToBottomAffordance
};